import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import '../assets/FinanceICDashboard.css';

const FinanceICDashboard = () => {
  const [activeTab, setActiveTab] = useState('assigned');
  const [assignedInvoices, setAssignedInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    tutee: ''
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Check user permissions and roles
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await adminApi.get('/auth/me');
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user info:', error);
        setError('Failed to load user information');
      }
    };

    fetchUserInfo();
  }, []);

  // Determine if user has dual roles (Finance IC + Profit Sharer)
  const isDualRole = user && 
    user.role === 'tutor' && 
    user.financeICSettings?.isEligible && 
    user.financeICSettings?.isActive && 
    user.isCoFounder;

  const isFinanceICOnly = user && 
    user.role === 'tutor' && 
    user.financeICSettings?.isEligible && 
    user.financeICSettings?.isActive && 
    !user.isCoFounder;

  const isProfitSharerOnly = user && user.isCoFounder && 
    (!user.financeICSettings?.isEligible || !user.financeICSettings?.isActive);

  // Set default tab based on user permissions
  useEffect(() => {
    if (!user) return;
    
    // For profit sharer only, default to 'all' tab
    if (isProfitSharerOnly) {
      setActiveTab('all');
    } else {
      // For Finance IC or dual role, default to 'assigned' tab
      setActiveTab('assigned');
    }
  }, [user, isProfitSharerOnly]);

  // Fetch assigned invoices (Finance IC view)
  const fetchAssignedInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        viewMode: 'assigned',
        ...filters
      });
      
      const response = await adminApi.get(`/tutee-invoices?${params}`);
      setAssignedInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching assigned invoices:', error);
      setError('Failed to load assigned invoices');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all invoices (Profit Sharer view)
  const fetchAllInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        viewMode: 'all',
        ...filters
      });
      
      const response = await adminApi.get(`/tutee-invoices?${params}`);
      setAllInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching all invoices:', error);
      setError('Failed to load all invoices');
    } finally {
      setLoading(false);
    }
  };

  // Load data based on active tab
  useEffect(() => {
    if (!user) return;

    if (activeTab === 'assigned' && (isDualRole || isFinanceICOnly)) {
      fetchAssignedInvoices();
    } else if (activeTab === 'all' && (isDualRole || isProfitSharerOnly)) {
      fetchAllInvoices();
    }
  }, [activeTab, filters, user, isDualRole, isFinanceICOnly, isProfitSharerOnly]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle bulk download of assigned invoices
  const handleBulkDownload = async () => {
    if (assignedInvoices.length === 0) {
      setError('No invoices available to download');
      return;
    }

    try {
      setIsDownloading(true);
      setDownloadProgress(0);
      setError(null);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + 15;
        });
      }, 200);

      // Get invoice IDs from filtered assigned invoices
      const invoiceIds = assignedInvoices.map(inv => inv._id);

      // Call bulk export API with Finance IC filter
      const response = await adminApi.post('/tutee-invoices/bulk-export', {
        invoiceIds
      }, {
        responseType: 'blob'
      });

      clearInterval(progressInterval);
      setDownloadProgress(100);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/zip' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename with month/year if filtered
      const monthName = filters.month 
        ? new Date(2024, filters.month - 1).toLocaleDateString('en-SG', { month: 'short' })
        : 'All';
      const yearStr = filters.year || 'All';
      link.download = `My_Invoices_${monthName}_${yearStr}.zip`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Reset progress after a short delay
      setTimeout(() => {
        setDownloadProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Error downloading invoices:', error);
      setError(
        error.response?.data?.message || 
        'Failed to download invoices. Please try again.'
      );
      setDownloadProgress(0);
    } finally {
      setIsDownloading(false);
    }
  };

  // Mark invoice as paid (only for assigned invoices)
  const handleMarkAsPaid = async (invoiceId, paymentDetails) => {
    try {
      await adminApi.post(`/tutee-invoices/${invoiceId}/mark-paid`, paymentDetails);
      
      // Refresh assigned invoices
      if (activeTab === 'assigned') {
        fetchAssignedInvoices();
      }
      
      // Show success message
      setError(null);
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      setError('Failed to mark invoice as paid');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-badge status-pending';
      case 'assigned': return 'status-badge status-assigned';
      case 'paid': return 'status-badge status-paid';
      case 'disputed': return 'status-badge status-disputed';
      case 'resolved': return 'status-badge status-resolved';
      default: return 'status-badge';
    }
  };

  // Get payment status badge class
  const getPaymentStatusBadgeClass = (paymentStatus) => {
    switch (paymentStatus) {
      case 'unpaid': return 'payment-status-badge payment-unpaid';
      case 'paid': return 'payment-status-badge payment-paid';
      case 'partial': return 'payment-status-badge payment-partial';
      default: return 'payment-status-badge';
    }
  };

  // Copy phone number to clipboard
  const copyPhoneToClipboard = (phone) => {
    navigator.clipboard.writeText(phone).then(() => {
      // Show temporary success message
      const button = document.activeElement;
      const originalText = button.textContent;
      button.textContent = '✓ Copied!';
      button.style.backgroundColor = '#4CAF50';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy phone number:', err);
      alert('Failed to copy phone number');
    });
  };

  // Handle view invoice
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  // Close invoice modal
  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  // Get unique family count
  const getUniqueFamilyCount = (invoices) => {
    const uniqueFamilies = new Set(
      invoices
        .filter(inv => inv.tutee?.familyNumber)
        .map(inv => inv.tutee.familyNumber)
    );
    return uniqueFamilies.size;
  };

  // Render invoice table
  const renderInvoiceTable = (invoices, isEditable = false) => {
    if (invoices.length === 0) {
      return (
        <div className="no-invoices">
          <p>No invoices found matching your criteria.</p>
        </div>
      );
    }

    return (
      <div className="invoice-table-container">
        <table className="invoice-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Parent Name</th>
              <th>Tutee</th>
              <th>Parent Email</th>
              <th>Parent Phone</th>
              <th>Amount</th>
              <th>Payment Status</th>
              <th>Due Date</th>
              {isEditable && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {invoices.map(invoice => (
              <tr key={invoice._id} className={isEditable ? 'editable-row' : 'readonly-row'}>
                <td>
                  <span className="invoice-number">{invoice.invoiceNumber}</span>
                </td>
                <td>
                  <span className="parent-name">{invoice.parent?.name || 'N/A'}</span>
                </td>
                <td>
                  <div className="tutee-info">
                    <span className="tutee-name">{invoice.tutee?.name}</span>
                    <span className="tutee-code">{invoice.tutee?.code}</span>
                  </div>
                </td>
                <td>
                  <span className="parent-email">{invoice.parent?.email || 'N/A'}</span>
                </td>
                <td>
                  <div className="parent-phone-container">
                    {invoice.parent?.phone ? (
                      <>
                        <a 
                          href={`tel:${invoice.parent.phone}`} 
                          className="parent-phone-link"
                          title="Click to call"
                        >
                          {invoice.parent.phone}
                        </a>
                        <button
                          className="btn-copy-phone"
                          onClick={() => copyPhoneToClipboard(invoice.parent.phone)}
                          title="Copy phone number"
                        >
                          📋
                        </button>
                      </>
                    ) : (
                      <span className="parent-phone-na">N/A</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="invoice-amount">${formatCurrency(invoice.finalAmount)}</span>
                </td>
                <td>
                  <span className={getPaymentStatusBadgeClass(invoice.paymentStatus)}>
                    {invoice.paymentStatus}
                  </span>
                </td>
                <td>
                  <span className={`due-date ${new Date(invoice.paymentDueDate) < new Date() ? 'overdue' : ''}`}>
                    {formatDate(invoice.paymentDueDate)}
                  </span>
                </td>
                {isEditable && (
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleViewInvoice(invoice)}
                        title="View invoice details"
                      >
                        View
                      </button>
                      {invoice.paymentStatus !== 'paid' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleMarkAsPaid(invoice._id, {
                            paymentDate: new Date(),
                            paymentMethod: 'PayNow',
                            paymentNotes: 'Marked as paid by Finance IC'
                          })}
                          title="Mark this invoice as paid"
                        >
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render filters
  const renderFilters = () => (
    <div className="dashboard-filters">
      <div className="filter-group filter-month-year">
        <label>Month:</label>
        <select
          value={filters.month}
          onChange={(e) => handleFilterChange('month', e.target.value)}
        >
          <option value="">All Months</option>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(2024, i).toLocaleDateString('en-SG', { month: 'long' })}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group filter-month-year">
        <label>Year:</label>
        <select
          value={filters.year}
          onChange={(e) => handleFilterChange('year', e.target.value)}
        >
          {Array.from({ length: 5 }, (_, i) => {
            const year = new Date().getFullYear() - 2 + i;
            return (
              <option key={year} value={year}>{year}</option>
            );
          })}
        </select>
      </div>

      <div className="filter-group">
        <label>Payment Status:</label>
        <select
          value={filters.paymentStatus}
          onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
        >
          <option value="">All Payment Statuses</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Tutee:</label>
        <input
          type="text"
          placeholder="Search by tutee name or code"
          value={filters.tutee}
          onChange={(e) => handleFilterChange('tutee', e.target.value)}
        />
      </div>
    </div>
  );

  if (!user) {
    return <LoadingSpinner />;
  }

  // Check if user has any access to tutee invoices
  if (!isDualRole && !isFinanceICOnly && !isProfitSharerOnly) {
    return (
      <div className="finance-ic-dashboard">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access tutee invoices.</p>
          <p>Contact an administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="finance-ic-dashboard">
      <div className="dashboard-header">
        <h1>Tutee Invoice Dashboard</h1>
        <div className="user-role-info">
          {isDualRole && (
            <span className="role-badge dual-role">Finance IC + Profit Sharer</span>
          )}
          {isFinanceICOnly && (
            <span className="role-badge finance-ic">Finance IC</span>
          )}
          {isProfitSharerOnly && (
            <span className="role-badge profit-sharer">Profit Sharer</span>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        {(isDualRole || isFinanceICOnly) && (
          <button
            className={`tab-button ${activeTab === 'assigned' ? 'active' : ''}`}
            onClick={() => handleTabChange('assigned')}
          >
            <span className="tab-icon">📋</span>
            My Assigned Invoices
            <span className="tab-description">Edit & manage assigned invoices</span>
          </button>
        )}
        
        {(isDualRole || isProfitSharerOnly) && (
          <button
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            <span className="tab-icon">👁️</span>
            All Invoices (Read-Only)
            <span className="tab-description">View all invoices for profit analysis</span>
          </button>
        )}
      </div>

      {/* Access Level Indicator */}
      <div className="access-level-indicator">
        {activeTab === 'assigned' && (
          <div className="access-info editable">
            <span className="access-icon">✏️</span>
            <span>You have edit permissions for invoices assigned to you</span>
          </div>
        )}
        {activeTab === 'all' && (
          <div className="access-info readonly">
            <span className="access-icon">👁️</span>
            <span>Read-only access - View all invoices for profit analysis</span>
          </div>
        )}
      </div>

      {/* Filters */}
      {renderFilters()}

      {/* Error Message */}
      {error && <ErrorMessage message={error} />}

      {/* Loading Spinner */}
      {loading && <LoadingSpinner />}

      {/* Invoice Content */}
      {!loading && (
        <div className="dashboard-content">
          {activeTab === 'assigned' && (
            <div className="assigned-invoices-section">
              <div className="section-header">
                <div className="section-header-content">
                  <div className="section-header-text">
                    <h2>My Assigned Invoices</h2>
                    <p>Invoices assigned to you as Finance IC - you can edit and mark as paid</p>
                  </div>
                  <div className="section-header-actions">
                    <button
                      className="btn btn-download"
                      onClick={handleBulkDownload}
                      disabled={isDownloading || assignedInvoices.length === 0}
                      title="Download all your assigned invoices as a ZIP file"
                    >
                      {isDownloading ? (
                        <>
                          <span className="btn-spinner"></span>
                          Downloading... {downloadProgress}%
                        </>
                      ) : (
                        <>
                          <span className="download-icon">📥</span>
                          Download All My Invoices ({assignedInvoices.length})
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {isDownloading && (
                  <div className="download-progress-bar">
                    <div 
                      className="download-progress-fill" 
                      style={{ width: `${downloadProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
              {renderInvoiceTable(assignedInvoices, true)}
            </div>
          )}

          {activeTab === 'all' && (
            <div className="all-invoices-section">
              <div className="section-header">
                <h2>All Invoices (Read-Only)</h2>
                <p>Complete view of all tutee invoices for profit analysis</p>
              </div>
              {renderInvoiceTable(allInvoices, false)}
            </div>
          )}
        </div>
      )}

      {/* Summary Statistics */}
      {!loading && (
        <div className="dashboard-summary">
          {activeTab === 'assigned' && assignedInvoices.length > 0 && (
            <div className="summary-cards">
              <div className="summary-card">
                <h3>Total Assigned</h3>
                <span className="summary-value">{assignedInvoices.length}</span>
              </div>
              <div className="summary-card">
                <h3>Total Families</h3>
                <span className="summary-value">{getUniqueFamilyCount(assignedInvoices)}</span>
              </div>
              <div className="summary-card">
                <h3>Unpaid</h3>
                <span className="summary-value">
                  {assignedInvoices.filter(inv => inv.paymentStatus === 'unpaid').length}
                </span>
              </div>
              <div className="summary-card">
                <h3>Total Amount</h3>
                <span className="summary-value">
                  ${formatCurrency(assignedInvoices.reduce((sum, inv) => sum + inv.finalAmount, 0))}
                </span>
              </div>
            </div>
          )}

          {activeTab === 'all' && allInvoices.length > 0 && (
            <div className="summary-cards">
              <div className="summary-card">
                <h3>Total Invoices</h3>
                <span className="summary-value">{allInvoices.length}</span>
              </div>
              <div className="summary-card">
                <h3>Total Families</h3>
                <span className="summary-value">{getUniqueFamilyCount(allInvoices)}</span>
              </div>
              <div className="summary-card">
                <h3>Total Revenue</h3>
                <span className="summary-value">
                  ${formatCurrency(allInvoices.reduce((sum, inv) => sum + inv.finalAmount, 0))}
                </span>
              </div>
              <div className="summary-card">
                <h3>Collection Rate</h3>
                <span className="summary-value">
                  {allInvoices.length > 0 
                    ? `${Math.round((allInvoices.filter(inv => inv.paymentStatus === 'paid').length / allInvoices.length) * 100)}%`
                    : '0%'
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="invoice-modal-overlay" onClick={closeInvoiceModal}>
          <div className="invoice-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <h2>Invoice Details</h2>
              <button className="btn-close-modal" onClick={closeInvoiceModal}>×</button>
            </div>
            <div className="invoice-modal-body">
              <div className="invoice-detail-section">
                <h3>Invoice Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Invoice Number:</span>
                  <span className="detail-value">{selectedInvoice.invoiceNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Period:</span>
                  <span className="detail-value">
                    {new Date(selectedInvoice.year, selectedInvoice.month - 1).toLocaleDateString('en-SG', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Amount:</span>
                  <span className="detail-value invoice-amount">${formatCurrency(selectedInvoice.finalAmount)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Status:</span>
                  <span className={getPaymentStatusBadgeClass(selectedInvoice.paymentStatus)}>
                    {selectedInvoice.paymentStatus}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Due Date:</span>
                  <span className={`detail-value ${new Date(selectedInvoice.paymentDueDate) < new Date() ? 'overdue' : ''}`}>
                    {formatDate(selectedInvoice.paymentDueDate)}
                  </span>
                </div>
              </div>

              <div className="invoice-detail-section">
                <h3>Tutee Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{selectedInvoice.tutee?.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Code:</span>
                  <span className="detail-value tutee-code">{selectedInvoice.tutee?.code}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Family Number:</span>
                  <span className="detail-value">{selectedInvoice.tutee?.familyNumber || 'N/A'}</span>
                </div>
              </div>

              <div className="invoice-detail-section">
                <h3>Parent Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{selectedInvoice.parent?.name || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedInvoice.parent?.email || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <div className="detail-value">
                    {selectedInvoice.parent?.phone ? (
                      <div className="parent-phone-container">
                        <a 
                          href={`tel:${selectedInvoice.parent.phone}`} 
                          className="parent-phone-link"
                        >
                          {selectedInvoice.parent.phone}
                        </a>
                        <button
                          className="btn-copy-phone"
                          onClick={() => copyPhoneToClipboard(selectedInvoice.parent.phone)}
                        >
                          📋 Copy
                        </button>
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
              </div>

              {selectedInvoice.lessons && selectedInvoice.lessons.length > 0 && (
                <div className="invoice-detail-section">
                  <h3>Lessons ({selectedInvoice.lessons.length})</h3>
                  <div className="lessons-list">
                    {selectedInvoice.lessons.map((lesson, index) => (
                      <div key={index} className="lesson-item">
                        <div className="lesson-info">
                          <span className="lesson-subject">{lesson.subject}</span>
                          <span className="lesson-date">
                            {new Date(lesson.date).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })}
                          </span>
                        </div>
                        <div className="lesson-details">
                          <span className="lesson-duration">{lesson.duration}h</span>
                          <span className="lesson-mode">{lesson.mode}</span>
                          <span className="lesson-rate">${lesson.rate}/h</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="invoice-modal-footer">
              <button className="btn btn-secondary" onClick={closeInvoiceModal}>
                Close
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => window.open(`/invoice-preview/${selectedInvoice._id}`, '_blank')}
              >
                Open Full PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceICDashboard;